/**
 * 商城行为埋点 SDK(2026-08-21 新增)
 * 方案见工作区根《商城行为埋点实施方案》/测试.md:
 *  - track() 瞬时事件(导航点击/搜索提交/筛选项点击/设备绑定)
 *  - startSegment()/endSegment() 停留段(60s心跳,仅前台可见累计,单段30min封顶)
 *  - DeviceId 存 cookie(1年)——躲开 PC 端每周 localStorage.clear() 登出连带清设备归因
 *  - SPA 站内跳转 = 段边界(install 挂 router.beforeEach);pagehide+fetch keepalive 兜关标签/跳外链
 *  - 上报字段白名单:心跳行只带4字段,结束行不带 SDK 内部字段
 *  - RefKeyword 自动继承:详情段带"从哪个搜索词/条件组合点进来"
 *  - 2026-08-27 归一化:FilterDesc 段间/段内排序 + Keyword trim——防报表"同组合重复行"
 * 上报永远不抛错、不影响业务。
 */
import Cookies from 'js-cookie'

// ─── 事件类型(与后端 BehaviorEventType 枚举一致) ───
export const EV = {
  NAV_CLICK: 1,      // 导航分类点击(瞬时)
  SEARCH: 2,         // 搜索提交(瞬时)
  FILTER_DWELL: 3,   // 筛选组合/列表停留段(一次生效查询为一段)
  PRDT_DWELL: 4,     // 商品详情停留段
  PAGE_DWELL: 5,     // 通用页面停留段
  FILTER_CLICK: 6,   // 筛选项点击(含标准号,瞬时)
  DEVICE_BIND: 7,    // 设备绑定(登录成功)
  RECMD_EXPOSE: 8,   // 推荐位曝光(瞬时): 推荐模块内卡片进入可视区域
  RECMD_CLICK: 9     // 推荐位点击(瞬时): 点击推荐商品卡/系列卡
}

// ─── 筛选维度 code→中文(15组+终端模式扩展) ───
export const FILTER_LABELS = {
  bz: '标准', cz: '材质', cl: '材料', jb: '级别', bmcl: '表面处理',
  xh: '型号', zj: '直径', cd: '长度', yx: '牙型', pp: '品牌',
  ck: '仓库', yj: '牙距', nj: '内径', wj: '外径', ts: '特殊说明',
  db: '对边', hd: '厚度', dl: '大类', xl: '小类'
}

const DEVICE_KEY = 'behaviorDeviceId'
const REPORT_URL = 'api/Behavior/Report'
const MAX_BATCH = 20          // 单次上报条数上限(与后端限流对齐)
const HEARTBEAT = 60000       // 心跳60s
const MAX_DWELL = 1800        // 单段30min封顶

// 设备标识:cookie 存1年;旧 localStorage 值自动迁移
export function getDeviceId() {
  let id = Cookies.get(DEVICE_KEY)
  if (!id) {
    id = localStorage.getItem(DEVICE_KEY) || uuid()
    Cookies.set(DEVICE_KEY, id, { expires: 365, sameSite: 'Lax' })
  }
  return id
}

// ─── 上报字段白名单:不带 timer/dwell/lastTick 等 SDK 内部字段 ───
const FIELDS = ['SessionId', 'EventType', 'SourceType', 'ViewPath', 'Keyword', 'NavJson',
  'CategoryId', 'CategoryName', 'PrdtId', 'PrdtName', 'RefKeyword', 'FromPath',
  'FilterJson', 'FilterDesc', 'ResultCount', 'EnterTime']

let seg = null       // 当前停留段
let lastCtx = null   // 最近一次列表/搜索段上下文(详情归因用)
let lastFrom = ''    // 进入当前页面的来源路由(详情段FromPath用)

// 瞬时事件:导航/搜索/筛选点击/绑定
export function track(EventType, payload) {
  send([{ SessionId: uuid(), Action: 3, EventType, DwellSeconds: 0, ...normKeyword(payload) }])
}

// 开始停留段(自动先结束上一段)
export function startSegment(EventType, payload) {
  endSegment()
  seg = {
    SessionId: uuid(), EventType, ...normKeyword(payload),
    EnterTime: now(), dwell: 0, lastTick: Date.now()
  }
  if (!seg.FromPath) seg.FromPath = lastFrom
  if (EventType === EV.SEARCH || EventType === EV.FILTER_DWELL) {
    // 记住"当前在什么词/什么条件下"
    lastCtx = { Keyword: seg.Keyword || '', FilterDesc: seg.FilterDesc || '', ViewPath: seg.ViewPath || '' }
  }
  if (EventType === EV.PRDT_DWELL && !seg.RefKeyword && lastCtx) {
    // 详情段自动继承来源:搜索词→详情归因
    seg.RefKeyword = lastCtx.Keyword
    if (!seg.FromPath) seg.FromPath = lastCtx.ViewPath
  }
  seg.timer = setInterval(tick, HEARTBEAT)
}

// 心跳:仅可见时间累计(lastTick 只包含可见区间——隐藏切换时已结清)
function tick() {
  if (!seg || document.hidden) return
  seg.dwell = Math.min(seg.dwell + Math.floor((Date.now() - seg.lastTick) / 1000), MAX_DWELL)
  seg.lastTick = Date.now()
  if (seg.dwell >= MAX_DWELL) return endSegment()
  send([{ SessionId: seg.SessionId, Action: 2, EventType: seg.EventType, DwellSeconds: seg.dwell }])
}

// 结束段(补差额);幂等
export function endSegment(extra) {
  if (!seg) return
  clearInterval(seg.timer)
  if (!document.hidden) {
    seg.dwell = Math.min(seg.dwell + Math.floor((Date.now() - seg.lastTick) / 1000), MAX_DWELL)
  }
  send([rowOf(seg, { LeaveTime: now(), Action: 3, DwellSeconds: seg.dwell, ...(extra || {}) })])
  seg = null
}

// 回填 ResultCount / PrdtName 等(列表/详情响应回来后)
export function patchSegment(patch) {
  if (seg) Object.assign(seg, patch || {})
}

function rowOf(seg, extra) {
  return FIELDS.reduce((o, k) => {
    if (seg[k] !== undefined && seg[k] !== null && seg[k] !== '') o[k] = seg[k]
    return o
  }, extra)
}

// 可见性切换:隐藏时先结清这段可见差额,回前台重开计时区间
document.addEventListener('visibilitychange', () => {
  if (!seg) return
  if (document.hidden) {
    seg.dwell = Math.min(seg.dwell + Math.floor((Date.now() - seg.lastTick) / 1000), MAX_DWELL)
  }
  seg.lastTick = Date.now()
})
// 关标签/跳外链兜底(keepalive 保证发出)
window.addEventListener('pagehide', () => endSegment())

// ─── 筛选描述构建:groups=[{type, ids:'1,2', data:[{id,name}]}] ───
// 遍历实际传入 key,映射表查不到的维度降级显示 code 本身
// 2026-08-27 归一化:ids 段内排序+段间排序——多选勾选顺序/入口页 groups 顺序不同,同一组合恒定产出同一串(报表按串聚合,防"同组合重复行")
export function buildFilterDesc(groups) {
  return (groups || []).filter(g => g && g.ids !== undefined && g.ids !== null && String(g.ids) !== '' && String(g.ids) !== '0')
    .map(g => {
      const label = FILTER_LABELS[g.type] || g.type
      const names = String(g.ids).split(',').filter(v => v !== '' && v !== '0').sort(cmpIds).map(id => {
        const hit = (g.data || []).find(d => String(d.id) === String(id))
        return hit ? (hit.name || id) : id
      }).join('、')
      return names ? (label + ':' + names) : ''
    }).filter(v => v).sort().join(';')
}

// ids 排序:纯数字按数值升序,其余按字符串——保证同集合恒定序
function cmpIds(a, b) {
  if (/^\d+$/.test(a) && /^\d+$/.test(b)) return a - b
  return a < b ? -1 : a > b ? 1 : 0
}

// Keyword trim(2026-08-27):报表按词聚合,搜索词尾随空格会产生"看起来一样"的重复行;入段/瞬时事件统一在此归一(RefKeyword 经 lastCtx 自动继承)
function normKeyword(payload) {
  if (payload && typeof payload.Keyword === 'string') payload.Keyword = payload.Keyword.trim()
  return payload
}

// 节点完整名称:name+' '+typeDescribe(下拉展示即两行 GB51/小六角螺母,单记name报表只剩"GB51"看不出是什么)
export function nodeLabel(node) {
  if (!node) return ''
  return ((node.name || '') + (node.typeDescribe ? ' ' + node.typeDescribe : '')).trim()
}

// ─── 推荐位曝光:共享一个 IntersectionObserver,卡片进入视口(≥50%)触发回调一次 ───
let exposeIO = null
const exposeCbs = new WeakMap()

// el 进入可视区域时执行 cb 一次并自动解除观察;IO 不可用(老内核)立即兜底触发
export function onceExpose(el, cb) {
  if (!('IntersectionObserver' in window)) { cb(); return }
  if (!exposeIO) {
    exposeIO = new IntersectionObserver(entries => {
      entries.forEach(en => {
        if (!en.isIntersecting) return
        const fn = exposeCbs.get(en.target)
        if (fn) {
          exposeIO.unobserve(en.target)
          exposeCbs.delete(en.target)
          fn()
        }
      })
    }, { threshold: 0.5 })
  }
  exposeCbs.set(el, cb)
  exposeIO.observe(el)
}

// 元素卸载时解除观察(v-rec-expose unbind 防泄漏)
export function offExpose(el) {
  if (exposeIO && exposeCbs.has(el)) {
    exposeIO.unobserve(el)
    exposeCbs.delete(el)
  }
}

// ─── 安装:Vue 原型 + 曝光指令 + 路由段边界 + 登录绑定 ───
export function install(Vue, router) {
  Vue.prototype.$behavior = { track, startSegment, endSegment, patchSegment, getDeviceId, buildFilterDesc, nodeLabel, onceExpose, offExpose, EV }

  // 推荐位曝光埋点:v-rec-expose="载荷对象"({PrdtId, FilterJson})——元素进入视口(≥50%)时上报 EventType=8,每元素一次
  Vue.directive('rec-expose', {
    inserted(el, binding) {
      onceExpose(el, () => {
        try {
          track(EV.RECMD_EXPOSE, {
            SourceType: 1,
            ViewPath: router && router.currentRoute ? router.currentRoute.path : '',
            ...(binding.value || {})
          })
        } catch (e) { /* 忽略 */ }
      })
    },
    unbind(el) { offExpose(el) }
  })

  router.beforeEach((to, from, next) => {
    // SPA 站内跳转 = 段边界
    endSegment()
    lastFrom = from && from.path ? from.path : ''
    // 登录绑定:buyerInfo.id 变化时报一次(DeviceId↔BuyerId 关联,服务端落 BuyerId 即完成)
    try {
      const info = JSON.parse(localStorage.getItem('buyerInfo') || 'null')
      const buyerId = info && info.id ? info.id : 0
      if (localStorage.getItem('token') && buyerId && localStorage.getItem('behaviorBoundBuyer') != String(buyerId)) {
        localStorage.setItem('behaviorBoundBuyer', String(buyerId))
        track(EV.DEVICE_BIND, {})
      }
    } catch (e) { /* 忽略 */ }
    next()
  })
}

// ─── 上报 ───
function send(events) {
  try {
    const token = localStorage.getItem('token')
    const base = localStorage.getItem('baseUrl')
      || (localStorage.getItem('GlobalConfig') ? (JSON.parse(localStorage.getItem('GlobalConfig')).BASE_URL || '') : '')
    // 分批,单次≤20条
    for (let i = 0; i < events.length; i += MAX_BATCH) {
      const batch = events.slice(i, i + MAX_BATCH)
      fetch(base + REPORT_URL, {
        method: 'POST',
        keepalive: true, // 页面卸载后仍发出,且能带header(sendBeacon不能)
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: 'Bearer ' + token } : {})
        },
        body: JSON.stringify({ deviceId: getDeviceId(), events: batch })
      }).catch(() => { }) // 永不抛错
    }
  } catch (e) { /* 忽略 */ }
}

function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

function now() {
  const d = new Date()
  const p = n => (n < 10 ? '0' + n : '' + n)
  const ms = d.getMilliseconds()
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' +
    p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds()) + '.' + (ms < 10 ? '00' + ms : ms < 100 ? '0' + ms : ms)
}

export default { install, track, startSegment, endSegment, patchSegment, getDeviceId, buildFilterDesc, nodeLabel, onceExpose, offExpose, EV, FILTER_LABELS }
